import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../services/firebase';
import { query, collection, where, getDocs, doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc, serverTimestamp, addDoc, runTransaction, onSnapshot } from 'firebase/firestore';
import { useUserContext } from '../contexts/UserContext';
import { useUserTracks } from '../hooks/useUserTracks';
import TrackList from '../components/common/TrackList';
import PlaylistTab from '../components/common/PlaylistTab';
import LikedTracks from '../components/common/LikedTracks';
import StoriesArchiveTab from '../components/profile/StoriesArchiveTab';
import Feed from '../components/posts/Feed';
import CreatePostForm from '../components/posts/CreatePostForm';
import ReceivedGiftsTab from '../components/gifts/ReceivedGiftsTab';
import { signOut } from 'firebase/auth';
import { auth } from '../services/firebase';
import SendGiftModal from '../components/gifts/SendGiftModal';
import toast from 'react-hot-toast';

import default_picture from '../img/Default-Images/default-picture.svg';
import VerifiedBadge from '../components/common/VerifiedBadge';
import DiscovererBadge from '../components/common/DiscovererBadge';
import PageLoader from '../components/common/PageLoader';
import CreateStoryModal from '../components/stories/CreateStoryModal';
import StoryViewer from '../components/stories/StoryViewer';
import { subscribeToUserStories } from '../services/storiesService';
import NowPlayingBanner from '../components/profile/NowPlayingBanner';
import './Profile.css';

const MusicIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>;
const FeedIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>;
const GiftIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>;
const ActionsIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>;

const ProfilePage = ({ openBrowser, openShareModal }) => {
    const { userNickname } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, authLoading, refreshUser } = useUserContext();

    const [profileUser, setProfileUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('music');
    const [musicSubTab, setMusicSubTab] = useState('tracks');
    const [feedSubTab, setFeedSubTab] = useState('threads');
    const [isFollowing, setIsFollowing] = useState(false);
    const [isProcessingFollow, setIsProcessingFollow] = useState(false);
    const [isActionMenuOpen, setActionMenuOpen] = useState(false);
    const actionMenuRef = useRef(null);
    const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
    const [nowPlayingData, setNowPlayingData] = useState(null);
    const [isCreateStoryOpen, setIsCreateStoryOpen] = useState(false);
    const [isStoryViewerOpen, setIsStoryViewerOpen] = useState(false);
    const [profileStories, setProfileStories] = useState([]);

    const isOwnProfile = !userNickname || (profileUser && currentUser && profileUser.uid === currentUser.uid);
    const targetUserId = profileUser?.uid;

    const { tracks: topTracks, loading: loadingTop } = useUserTracks(targetUserId, { orderByField: 'playCount', orderByDirection: 'desc', limit: 5 });
    const { tracks: latestTracks, loading: loadingLatest } = useUserTracks(targetUserId, { orderByField: 'createdAt', orderByDirection: 'desc', limit: 5 });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setActionMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [actionMenuRef]);

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            try {
                let userToFetchId;
                let userToFetchData;

                if (userNickname) {
                    const usersQuery = query(collection(db, 'users'), where('nickname', '==', userNickname));
                    const querySnapshot = await getDocs(usersQuery);
                    if (!querySnapshot.empty) {
                        const userDoc = querySnapshot.docs[0];
                        userToFetchId = userDoc.id;
                        userToFetchData = userDoc.data();
                    } else {
                        throw new Error("Користувача не знайдено");
                    }
                } else if (currentUser) {
                    // /profile route — redirect to canonical /:nickname URL
                    if (currentUser.nickname) {
                        navigate(`/${currentUser.nickname}`, { replace: true });
                        return;
                    }
                    userToFetchId = currentUser.uid;
                    userToFetchData = currentUser;
                } else {
                    setLoading(false);
                    if (!authLoading) navigate('/login');
                    return;
                }

                setProfileUser({ ...userToFetchData, uid: userToFetchId });

                if (currentUser && userToFetchId !== currentUser.uid) {
                    setIsFollowing(currentUser.following?.includes(userToFetchId));
                }
            } catch (e) {
                console.error("Помилка завантаження даних користувача:", e);
                setProfileUser(null);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            fetchUserData();
        }
    }, [userNickname, currentUser, authLoading, navigate]);

    // Real-time listener for nowPlaying field — updates the banner widget live
    useEffect(() => {
        if (!profileUser?.uid) {
            setNowPlayingData(null);
            return;
        }
        const userRef = doc(db, 'users', profileUser.uid);
        const unsubscribe = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
                setNowPlayingData(snap.data().nowPlaying || null);
            } else {
                setNowPlayingData(null);
            }
        });
        return () => unsubscribe();
    }, [profileUser?.uid]);

    // Real-time listener for this profile user's active stories
    useEffect(() => {
        if (!profileUser?.uid) {
            setProfileStories([]);
            return;
        }
        const unsubscribe = subscribeToUserStories(profileUser.uid, (stories) => {
            setProfileStories(stories);
        });
        return () => unsubscribe();
    }, [profileUser?.uid]);

    const handleFollowToggle = async () => {
        if (!currentUser || !profileUser || isProcessingFollow || isOwnProfile) return;
        setIsProcessingFollow(true);
        const profileUserRef = doc(db, 'users', profileUser.uid);
        const currentUserRef = doc(db, 'users', currentUser.uid);
        try {
            if (isFollowing) {
                await updateDoc(profileUserRef, { followers: arrayRemove(currentUser.uid) });
                await updateDoc(currentUserRef, { following: arrayRemove(profileUser.uid) });
            } else {
                await updateDoc(profileUserRef, { followers: arrayUnion(currentUser.uid) });
                await updateDoc(currentUserRef, { following: arrayUnion(profileUser.uid) });
                const notificationRef = collection(db, 'users', profileUser.uid, 'notifications');
                await addDoc(notificationRef, {
                    type: 'new_follower',
                    fromUser: { uid: currentUser.uid, nickname: currentUser.nickname, photoURL: currentUser.photoURL },
                    entityId: currentUser.uid, entityLink: `/${currentUser.nickname}`,
                    timestamp: serverTimestamp(), read: false
                });
            }
            await refreshUser();
            setProfileUser(prev => ({ ...prev, followers: isFollowing ? prev.followers.filter(id => id !== currentUser.uid) : [...(prev.followers || []), currentUser.uid] }));
            setIsFollowing(!isFollowing);
        } catch (error) {
            console.error('Помилка підписки/відписки:', error);
        } finally {
            setIsProcessingFollow(false);
        }
    };

    const handleStartConversation = async () => {
        if (!currentUser || !profileUser || isOwnProfile) return;
        const chatId = [currentUser.uid, profileUser.uid].sort().join('_');
        const chatRef = doc(db, 'chats', chatId);
        try {
            const chatSnap = await getDoc(chatRef);
            if (!chatSnap.exists()) {
                const dataToCreate = {
                    isGroup: false,
                    participants: [currentUser.uid, profileUser.uid],
                    participantInfo: [
                        { uid: currentUser.uid, displayName: currentUser.displayName, photoURL: currentUser.photoURL || null },
                        { uid: profileUser.uid, displayName: profileUser.displayName, photoURL: profileUser.photoURL || null }
                    ],
                    lastMessage: null,
                    lastUpdatedAt: serverTimestamp(),
                    unreadCounts: { [currentUser.uid]: 0, [profileUser.uid]: 0 },
                };
                await setDoc(chatRef, dataToCreate);
            }
            navigate('/messages', { state: { conversationId: chatId } });
        } catch (error) {
            console.error("Помилка створення чату:", error);
        }
    };

    const handleLogout = () => {
        if (!window.confirm('Ви впевнені, що хочете вийти?')) return;
        signOut(auth).then(() => navigate('/'));
    };

    const handleSendGift = async (giftParams, recipientUser) => {
        if (!currentUser) {
            toast.error("Будь ласка, увійдіть, щоб дарувати подарунки.");
            return;
        }
        if (currentUser.uid === recipientUser.uid) {
            toast.error("Ви не можете дарувати подарунки самому собі.");
            return;
        }

        const senderRef = doc(db, 'users', currentUser.uid);
        const recipientRef = doc(db, 'users', recipientUser.uid);

        try {
            await runTransaction(db, async (transaction) => {
                const senderDoc = await transaction.get(senderRef);
                if (!senderDoc.exists()) throw new Error("Ваш профіль не знайдено.");

                // ЧИТАННЯ (Reads) повинні виконуватись ДО ЗАПИСУ (Writes)
                const recipientDoc = await transaction.get(recipientRef);

                const senderBalance = senderDoc.data().notesBalance || 0;

                // Перевірка балансу 
                if (senderBalance < giftParams.price) throw new Error("Недостатньо Нот на балансі.");

                // ЗАПИСИ (Writes)
                // Віднімаємо ноти відправнику
                transaction.update(senderRef, { notesBalance: senderBalance - giftParams.price });

                // Нараховуємо ноти артисту (тимчасово 100%, потім зробимо 90/10)
                const recipientBalance = recipientDoc.exists() ? (recipientDoc.data().notesBalance || 0) : 0;
                if (recipientDoc.exists()) {
                    transaction.update(recipientRef, { notesBalance: recipientBalance + giftParams.price });
                }

                const recipientGiftRef = doc(collection(recipientRef, 'receivedGifts'));
                transaction.set(recipientGiftRef, {
                    giftId: giftParams.id,
                    giftName: giftParams.name, // object {uk, en}
                    giftLottieUrl: giftParams.lottieUrl || null,
                    giftType: giftParams.type || 'regular',
                    fromUserId: giftParams.isAnonymous ? null : currentUser.uid,
                    fromUserName: giftParams.isAnonymous ? 'Анонім' : currentUser.displayName,
                    message: giftParams.message || '',
                    isAnonymous: giftParams.isAnonymous || false,
                    receivedAt: serverTimestamp()
                });

                // Створення сповіщення для отримувача
                const notificationRef = doc(collection(recipientRef, 'notifications'));
                transaction.set(notificationRef, {
                    type: 'gift_received',
                    fromUser: giftParams.isAnonymous ? { uid: 'anonymous', nickname: 'Анонім', photoURL: null } : { uid: currentUser.uid, nickname: currentUser.nickname, photoURL: currentUser.photoURL },
                    entityId: recipientGiftRef.id,
                    entityTitle: giftParams.name, // об'єкт {uk, en}
                    entityThumbnail: giftParams.lottieUrl || null,
                    timestamp: serverTimestamp(),
                    read: false
                });
            });

            const giftDisplayName = giftParams.name?.uk || giftParams.name?.en || 'Подарунок';
            toast.success(`Подарунок "${giftDisplayName}" успішно відправлено до ${recipientUser.displayName}!`);
            await refreshUser();

        } catch (error) {
            console.error("Помилка відправки подарунка:", error);
            toast.error(error.message || "Не вдалося відправити подарунок.");
        }
    };

    const renderTabContent = () => {
        if (!profileUser) return null;

        switch (activeTab) {
            case 'music':
                const hasTracks = (profileUser.tracksCount || 0) > 0;
                return (
                    <div>
                        <div className="profile-feed-subtabs" style={{ marginBottom: '1.5rem' }}>
                            <button className={`profile-feed-subtab${musicSubTab === 'tracks' ? ' active' : ''}`} onClick={() => setMusicSubTab('tracks')}>Треки</button>
                            <button className={`profile-feed-subtab${musicSubTab === 'albums' ? ' active' : ''}`} onClick={() => setMusicSubTab('albums')}>Альбоми</button>
                            <button className={`profile-feed-subtab${musicSubTab === 'playlists' ? ' active' : ''}`} onClick={() => setMusicSubTab('playlists')}>Плейлисти</button>
                        </div>

                        {musicSubTab === 'tracks' && (
                            <>
                                {hasTracks && (
                                    <div className="profile-music-layout">
                                        <div className="profile-section">
                                            <h3 className="profile-section-title">Топ треки</h3>
                                            <TrackList initialTracks={topTracks} isLoading={loadingTop} />
                                        </div>
                                        <div className="profile-section">
                                            <h3 className="profile-section-title">Останні треки</h3>
                                            <TrackList initialTracks={latestTracks} isLoading={loadingLatest} />
                                        </div>
                                    </div>
                                )}
                                <div className="profile-section">
                                    <h3 className="profile-section-title">Вподобана музика</h3>
                                    <LikedTracks user={profileUser} />
                                </div>
                            </>
                        )}

                        {musicSubTab === 'albums' && (
                            <div className="page-profile-tab-placeholder">Альбоми цього користувача будуть відображатися тут.</div>
                        )}

                        {musicSubTab === 'playlists' && (
                            <PlaylistTab userId={profileUser.uid} />
                        )}
                    </div>
                );
            case 'gifts':
                return <ReceivedGiftsTab userId={profileUser.uid} isOwnProfile={isOwnProfile} onSendGiftClick={() => setIsGiftModalOpen(true)} />;
            case 'feed':
                return (
                    <div>
                        {isOwnProfile && <CreatePostForm />}
                        <div className="profile-feed-subtabs">
                            <button className={`profile-feed-subtab${feedSubTab === 'threads' ? ' active' : ''}`} onClick={() => setFeedSubTab('threads')}>Ланцюжок</button>
                            <button className={`profile-feed-subtab${feedSubTab === 'replies' ? ' active' : ''}`} onClick={() => setFeedSubTab('replies')}>Відповіді</button>
                            <button className={`profile-feed-subtab${feedSubTab === 'media' ? ' active' : ''}`} onClick={() => setFeedSubTab('media')}>Медіафайли</button>
                            <button className={`profile-feed-subtab${feedSubTab === 'reposts' ? ' active' : ''}`} onClick={() => setFeedSubTab('reposts')}>Репости</button>
                        </div>
                        {(feedSubTab === 'threads' || feedSubTab === 'media') && (
                            <Feed
                                userId={profileUser.uid}
                                feedFilter={feedSubTab}
                                openBrowser={openBrowser}
                                openShareModal={openShareModal}
                            />
                        )}
                        {(feedSubTab === 'replies' || feedSubTab === 'reposts') && (
                            <div className="feed-subtab-placeholder">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
                                <p>Цей розділ буде доступний найближчим часом</p>
                            </div>
                        )}
                    </div>
                );
            case 'stories':
                return <StoriesArchiveTab profileUser={profileUser} />;
            default:
                return null;
        }
    };

    if (loading || authLoading) return <PageLoader text="Завантаження профілю..." />;
    if (!profileUser) return <div>Користувача не знайдено.</div>;

    return (
        <>
            <div className="page-profile-container">
                <div className="page-profile-header">
                    <div className="page-profile-background" style={{ backgroundImage: `url(${profileUser.backgroundImage || ''})` }}>
                        {/* Now Playing banner widget — shown when user is broadcasting */}
                        {nowPlayingData && <NowPlayingBanner nowPlaying={nowPlayingData} />}
                        {/* Actions icon — top-right of banner, other profiles only */}
                        {!isOwnProfile && (
                            <div className="profile-banner-actions" ref={actionMenuRef}>
                                <button
                                    className="profile-banner-action-btn"
                                    onClick={() => setActionMenuOpen(!isActionMenuOpen)}
                                    aria-label="Дії"
                                >
                                    <ActionsIcon />
                                </button>
                                <div className={`profile-actions-dropdown ${isActionMenuOpen ? 'visible' : ''}`}>
                                    <button className="dropdown-action-button" onClick={() => { setIsGiftModalOpen(true); setActionMenuOpen(false); }}>
                                        Надіслати подарунок
                                    </button>
                                    <button className="dropdown-action-button danger" onClick={() => { alert('Функціонал блокування в розробці'); setActionMenuOpen(false); }}>
                                        Заблокувати
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="page-profile-header-content">
                        <div className="page-profile-avatar-wrapper">
                            {/* Gradient ring — visible when profile has active stories */}
                            <div
                                className={`profile-avatar-story-ring ${profileStories.length > 0 ? 'profile-avatar-story-ring--active' : ''}`}
                                onClick={profileStories.length > 0 ? () => setIsStoryViewerOpen(true) : undefined}
                                role={profileStories.length > 0 ? 'button' : undefined}
                                tabIndex={profileStories.length > 0 ? 0 : undefined}
                                onKeyDown={profileStories.length > 0 ? (e) => e.key === 'Enter' && setIsStoryViewerOpen(true) : undefined}
                                aria-label={profileStories.length > 0 ? `Переглянути сторіс ${profileUser.displayName}` : undefined}
                            >
                                <img src={profileUser.photoURL || default_picture} alt="Avatar" className="page-profile-avatar" onError={(e) => { e.target.onerror = null; e.target.src = default_picture; }} />
                            </div>
                            {/* + button — always visible on own profile to add a new story */}
                            {isOwnProfile && (
                                <button
                                    className="profile-add-story-btn"
                                    onClick={(e) => { e.stopPropagation(); setIsCreateStoryOpen(true); }}
                                    title="Додати сторіс"
                                    aria-label="Додати сторіс"
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M7 1v12M1 7h12" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        {/* Desktop: actions sit beside avatar */}
                        <div className="page-profile-actions-group profile-actions-desktop-only">
                            {isOwnProfile ? (
                                <>
                                    <button className="page-profile-secondary-button" onClick={() => navigate('/settings')}>Редагувати профіль</button>
                                    <button className="page-profile-logout-button" onClick={handleLogout}>Вийти</button>
                                </>
                            ) : (
                                <>
                                    <button className="page-profile-action-button" onClick={handleFollowToggle} disabled={isProcessingFollow}>
                                        {isFollowing ? "Відписатися" : "Підписатися"}
                                    </button>
                                    <button className="page-profile-secondary-button" onClick={handleStartConversation}>
                                        Повідомлення
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="page-profile-info">
                        <h2 className="page-profile-display-name">
                            {/* [ім'я] [🔮 кастомні емоджі тут у майбутньому] [галочка] */}
                            {profileUser.displayName || 'No Name'}
                            {profileUser.roles?.includes('verified') && <VerifiedBadge size="lg" />}
                            {profileUser.badges?.includes('discoverer') && <DiscovererBadge size="lg" />}
                        </h2>
                        <p className="page-profile-nickname">@{profileUser.nickname}</p>
                        <p className="page-profile-description">{profileUser.description || 'No description'}</p>
                        <div className="page-profile-stats">
                            <Link to={`/${profileUser.nickname}/followers`} className="page-profile-stat-item page-profile-stat-link"><strong>{profileUser.followers?.length || 0}</strong><span>Підписники</span></Link>
                            <Link to={`/${profileUser.nickname}/following`} className="page-profile-stat-item page-profile-stat-link"><strong>{profileUser.following?.length || 0}</strong><span>Підписки</span></Link>
                        </div>
                        {/* Mobile: actions below stats */}
                        <div className="page-profile-actions-group profile-actions-mobile-only">
                            {isOwnProfile ? (
                                <>
                                    <button className="page-profile-secondary-button" onClick={() => navigate('/settings')}>Редагувати профіль</button>
                                    <button className="page-profile-logout-button" onClick={handleLogout}>Вийти</button>
                                </>
                            ) : (
                                <>
                                    <button className="page-profile-action-button" onClick={handleFollowToggle} disabled={isProcessingFollow}>
                                        {isFollowing ? "Відписатися" : "Підписатися"}
                                    </button>
                                    <button className="page-profile-secondary-button" onClick={handleStartConversation}>
                                        Повідомлення
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="page-profile-tabs">
                        <button className={`page-profile-tab-button ${activeTab === 'music' ? 'active' : ''}`} onClick={() => setActiveTab('music')}><MusicIcon /> Музика</button>
                        <button className={`page-profile-tab-button ${activeTab === 'feed' ? 'active' : ''}`} onClick={() => setActiveTab('feed')}><FeedIcon /> Стрічка</button>
                        <button className={`page-profile-tab-button ${activeTab === 'gifts' ? 'active' : ''}`} onClick={() => setActiveTab('gifts')}><GiftIcon /> Подарунки</button>
                        {isOwnProfile && (
                            <button className={`page-profile-tab-button ${activeTab === 'stories' ? 'active' : ''}`} onClick={() => setActiveTab('stories')}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg> Історії
                            </button>
                        )}
                    </div>
                </div>

                <main className="page-profile-main-content">
                    <div className="page-profile-tab-content">
                        {renderTabContent()}
                    </div>
                </main>
            </div>

            {isGiftModalOpen && (
                <SendGiftModal
                    recipient={profileUser}
                    onClose={() => setIsGiftModalOpen(false)}
                    onGiftSendInitiated={handleSendGift}
                />
            )}

            {isCreateStoryOpen && (
                <CreateStoryModal onClose={() => setIsCreateStoryOpen(false)} />
            )}

            {/* Story Viewer — opens when avatar ring is clicked and stories exist */}
            {isStoryViewerOpen && profileStories.length > 0 && (
                <StoryViewer
                    groups={[{
                        uid: profileUser.uid,
                        userNickname: profileUser.nickname,
                        userDisplayName: profileUser.displayName,
                        userPhotoURL: profileUser.photoURL || null,
                        stories: profileStories,
                    }]}
                    initialGroupIndex={0}
                    currentUserUid={currentUser?.uid}
                    onClose={() => setIsStoryViewerOpen(false)}
                    onStoriesSeen={() => { }}
                />
            )}
        </>
    );
};

export default ProfilePage;